import { UHF, TRIP_DETAIL } from '../constants';
import { getCurrentLocale, translate } from './LocaleProvider';
import 'intl';
import 'intl/locale-data/jsonp/en';
import 'intl/locale-data/jsonp/vi';
import 'intl-list-format';
import 'intl-list-format/locale-data/en';
import 'intl-list-format/locale-data/vi';

export const numberFormat = (
  number = 0,
  formatOptions = {},
  locale = getCurrentLocale() || UHF.DEFAULT_LANGUAGE
) =>
  new Intl.NumberFormat(locale, { style: 'decimal', ...formatOptions }).format(
    number
  );

export const listFormat = (
  list = [],
  formatOptions = {},
  locale = getCurrentLocale() || UHF.DEFAULT_LANGUAGE
) =>
  new Intl.ListFormat(locale, {
    style: 'long',
    type: 'unit',
    ...formatOptions,
  }).format(list);

export const getDisplayGuests = (adults, children, infants = 0) => {
  const adultsText = translate('common.adults', Number(adults));
  const childrenText =
    children && children > 0
      ? `, ${translate('common.children', children + infants)}`
      : '';

  return `${adultsText}${childrenText}`;
};

export const getRedeemText = availableBalance => {
  return `${translate('common.bigPoints.redeem.text', {
    available_balance: availableBalance,
  })}`;
};

export const getUsableBigPointsText = usableBigPoints => {
  return `${translate('common.bigpointsTooltip.info', {
    usable_percent: usableBigPoints,
  })}`;
};

export const getDisplaySingularGuest = (adults, children, infants = 0) => {
  const adultsText = translate('common.singularAdult', Number(adults));
  const childrenText =
    children && children > 0
      ? `, ${translate('common.singularChild', children + infants)}`
      : '';

  return `${adultsText}${childrenText}`;
};

export const getDisplayHotelGuests = ({ adults, children, infants }) => {
  return getDisplayGuests(
    adults,
    children && children.length,
    infants && infants.length
  );
};

export const countOccupancy = occupancy =>
  occupancy
    ? occupancy
        .toString()
        .split(',')
        .filter(element => element.trim().length > 0).length
    : 0;

export const getDisplaySnapGuests = ({ adults, children, infants }) => {
  const numberAdults =
    (adults &&
      (adults + '')
        .split(',')
        .filter(i => {
          i = i.trim();
          return isNaN(i) ? 0 : i;
        })
        .reduce((a, b) => parseInt(a) + parseInt(b), 0)) ||
    0;
  const numberChildren = countOccupancy(children);
  const numberInfants = countOccupancy(infants);
  return getDisplaySingularGuest(numberAdults, numberChildren + numberInfants);
};

export const getDisplayOccupancyAmenity = ({ adults, children }) => {
  return (adults && adults > 0) || (children && children > 0)
    ? `${translate('roomCard.max')}${
        adults && adults > 0
          ? ` ${translate('common.adults', { smart_count: adults })}`
          : ''
      }${
        children && children > 0
          ? ` ${translate('common.children', { smart_count: children })}`
          : ''
      }`
    : '';
};

export const getDisplayAddress = ({
  line_1 = '',
  line_2 = '',
  city = '',
  state_province_name = '',
  country = '',
  postal_code = '',
}) => {
  const addressArray = [line_1, line_2, city, state_province_name, country];
  return `${addressArray
    .map(item => item.trim())
    .filter(Boolean)
    .join(', ')} ${postal_code}`;
};

export const getRoomBedTypeAndRoomSize = ({ bedSize, squareMeterArea }) =>
  `${bedSize ? bedSize : ''}${bedSize && squareMeterArea ? ' | ' : ''}${
    squareMeterArea
      ? translate('roomCard.squareMeter', { squareMeterArea })
      : ''
  }`;

export const createScopedTranslate = scopedKey => (key, ...restArgs) =>
  translate(`${scopedKey}.${key}`, ...restArgs);

export const getCountryFromLocale = ({ languageId, countries }) => {
  const languageCountryCode = languageId.split('-')[1];
  const country = countries.find(
    ({ countryCode }) => countryCode === languageCountryCode.toUpperCase()
  );
  return country;
};

export const getDefaultNationality = ({
  countryCode,
  languageId,
  countries,
}) => {
  let defaultNationality = countries.find(
    ({ countryCode: cc }) => cc === countryCode
  ) || { countryCode: TRIP_DETAIL.DEFAULT_COUNTRY_CODE };

  if (languageId && languageId !== 'en-gb') {
    defaultNationality =
      getCountryFromLocale({ languageId, countries }) || defaultNationality;
  }

  return defaultNationality;
};

export const convertTagToTranslationID = tag => {
  if (typeof tag === 'string') {
    return tag;
  }
  const { translationId = '', numberOfRooms, value = '' } = tag;
  const translatedTag = translate(translationId, {
    smart_count: numberOfRooms,
  });
  if (translatedTag && translatedTag !== translationId) {
    return translatedTag;
  }
  return value;
};

const CHINA_LOCALES = ['zh-cn', 'zh-tw', 'zh-hk'];
export const getDiscountPercentageLocale = (percent, locale) => {
  if (CHINA_LOCALES.includes(locale)) {
    return (100 - percent) / 10;
  }
  return percent;
};
